/**
 * «Invitar al panel» — admin, desde el negocio (FR-001a).
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { useSesion } from '../../auth'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { api, ErrorApi } from '../../lib/api'

export function InvitarNegocio() {
    const { accountId } = useParams()
    const id = Number(accountId)
    const { data: sesion } = useSesion()
    const [email, setEmail] = useState('')
    const [aviso, setAviso] = useState<string | null>(null)

    const contacto = useQuery({
        queryKey: ['contacto', id],
        queryFn: () =>
            api.get<{ accountId: number; contacto: { email?: string } }>(`/negocios/${id}/contacto`),
        enabled: Number.isFinite(id) && id > 0 && sesion?.role === 'admin',
    })

    useEffect(() => {
        const e = contacto.data?.contacto.email
        if (e) setEmail(e)
    }, [contacto.data])

    const [revokeId, setRevokeId] = useState('')
    const [avisoRevoke, setAvisoRevoke] = useState<string | null>(null)

    const mut = useMutation({
        mutationFn: () =>
            api.post<{ id: number; email: string }>('/usuarios', {
                email,
                nombre: email.split('@')[0] || email,
                role: 'cliente',
                accountIds: [id],
            }),
        onSuccess: (r) => setAviso(`Invitación enviada a ${r.email} (id ${r.id})`),
        onError: (e: Error) =>
            setAviso(e instanceof ErrorApi ? e.message : e.message || 'Error al invitar'),
    })

    const revocar = useMutation({
        mutationFn: () =>
            api.post<{ id: number; status: string }>(`/usuarios/${Number(revokeId)}/revoke`),
        onSuccess: (r) => setAvisoRevoke(`Usuario ${r.id} revocado`),
        onError: (e: Error) =>
            setAvisoRevoke(e instanceof ErrorApi ? e.message : 'No se pudo revocar'),
    })

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }
    if (!Number.isFinite(id) || id < 1) {
        return <p className="text-sm text-neutral-500">Negocio no válido</p>
    }

    return (
        <div className="mx-auto max-w-lg space-y-4" data-testid="invitar-negocio">
            <Link to={`/business/${id}`} className="text-xs text-neutral-500 hover:text-neutral-300">
                ← Negocio
            </Link>
            <h2 className="text-lg font-semibold text-neutral-100">Invitar al panel</h2>
            <p className="text-xs text-neutral-500">
                El alta no envía acceso. Confirma el correo (prellenado desde contacto) y envía la
                invitación. Caduca en 24 h.
            </p>
            <div className="space-y-2">
                <Input
                    aria-label="Email del cliente"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                    type="button"
                    className="min-h-11 w-full md:w-auto"
                    disabled={mut.isPending || !email}
                    onClick={() => mut.mutate()}
                >
                    {mut.isPending ? 'Enviando…' : 'Enviar invitación'}
                </Button>
            </div>
            {aviso && (
                <p role="status" className="text-xs text-neutral-300">
                    {aviso}
                </p>
            )}

            <hr className="border-neutral-800" />
            <h3 className="text-sm text-neutral-200">Revocar acceso</h3>
            <p className="text-xs text-neutral-500">Corta la sesión al momento (FR-007).</p>
            <div className="flex flex-col gap-2 md:flex-row">
                <Input
                    aria-label="Id de usuario"
                    placeholder="id usuario"
                    value={revokeId}
                    onChange={(e) => setRevokeId(e.target.value)}
                    className="min-w-0"
                />
                <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11 md:min-h-0"
                    disabled={revocar.isPending || !revokeId}
                    onClick={() => revocar.mutate()}
                >
                    Revocar
                </Button>
            </div>
            {avisoRevoke && (
                <p role="status" className="text-xs text-neutral-300">
                    {avisoRevoke}
                </p>
            )}
        </div>
    )
}
